import type { ReactNode } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@/icons';
import styles from '@/components/jog/jog.module.css';

interface SelectorProps {
  label: string;
  options: string[];
  activeIndex: number;
  onChange: (index: number) => void;
  /** Optional button rendered to the right of the carousel bar. */
  action?: ReactNode;
  /**
   * When set, only this many items are visible at once, centered around the
   * active index (carousel / sliding-window behaviour). Undefined = show all.
   */
  windowSize?: number;
}

function getWindow(
  options: string[],
  activeIndex: number,
  windowSize: number,
): { opt: string; originalIndex: number; dist: number }[] {
  const half = Math.floor(windowSize / 2);
  let start = activeIndex - half;
  let end = activeIndex + half;

  if (start < 0) {
    end -= start;
    start = 0;
  }
  if (end >= options.length) {
    start -= end - options.length + 1;
    end = options.length - 1;
  }
  start = Math.max(0, start);

  return options.slice(start, end + 1).map((opt, i) => ({
    opt,
    originalIndex: start + i,
    dist: Math.min(Math.abs(start + i - activeIndex), 2),
  }));
}

/** Carousel-style picker: active item centered + highlighted, optional sliding window. */
export function Selector({
  label,
  options,
  activeIndex,
  onChange,
  action,
  windowSize,
}: SelectorProps): JSX.Element {
  const items =
    windowSize !== undefined
      ? getWindow(options, activeIndex, windowSize)
      : options.map((opt, i) => ({
          opt,
          originalIndex: i,
          dist: Math.min(Math.abs(i - activeIndex), 2),
        }));

  return (
    <div className={styles.selector}>
      <span className={styles.selectorLabel}>{label}</span>
      <div className={styles.selectorRow}>
        <div className={styles.selectorBar}>
          <button
            className={styles.selectorChevron}
            type="button"
            aria-label={`Previous ${label}`}
            onClick={() => onChange(Math.max(0, activeIndex - 1))}
          >
            <ChevronLeftIcon width={28} height={28} />
          </button>
          <div className={styles.selectorValues}>
            {items.map(({ opt, originalIndex, dist }) => (
              <button
                key={originalIndex}
                type="button"
                className={[
                  styles.selectorValue,
                  styles[`selectorValueD${dist}`],
                  dist === 0 ? styles.selectorValueActive : '',
                ].join(' ')}
                onClick={() => onChange(originalIndex)}
              >
                {opt}
              </button>
            ))}
          </div>
          <button
            className={styles.selectorChevron}
            type="button"
            aria-label={`Next ${label}`}
            onClick={() => onChange(Math.min(options.length - 1, activeIndex + 1))}
          >
            <ChevronRightIcon width={28} height={28} />
          </button>
        </div>
        {action}
      </div>
    </div>
  );
}
